import firebase from '../firebase/compat'
import { db } from '../firebase/config'
import { client } from '../hooks/Client'

/**
 * Markeert één document als verwijderd in plaats van het echt weg te gooien
 * (ADR-008). De leesfilter in `useFirestoreQuery` laat zulke documenten
 * vervolgens overal buiten beeld, dus voor de gebruiker is het weg — maar het
 * is terug te halen via de prullenbak.
 *
 * Er wordt ook een regel bijgeschreven in `DeletedItems`. Firestore kan niet
 * over collecties heen zoeken, dus zonder die registratie zou de prullenbak
 * tientallen collecties apart moeten bevragen — met een samengestelde index per
 * collectie, en nog steeds blind voor de documenten die `Deleted` als tekst
 * bewaren. Eén registratie is één query.
 *
 * Heeft het document kinderen die mee moeten, gebruik dan een helper die die
 * keten kent, zoals `adressHelpers/softDeleteAdress`, en geef de kinderen
 * `parentId` mee zodat de prullenbak ze als één regel toont.
 *
 * `DeletedAt` gebruikt serverTimestamp() en niet de `timestamp` uit
 * firebase/config: die staat vast op het moment dat de pagina werd geladen.
 * AI-CONTEXT: See docs/decisions.md (ADR-008), docs/plans/prullenbak.md en
 * docs/pitfalls.md#state-stale-module-level-timestamp
 *
 * @param {object} params
 * @param {string} params.collection - Naam van de Firestore-collectie
 * @param {string} params.docid - Firestore-document-id
 * @param {string} [params.authId] - Wie het verwijdert
 * @param {string} [params.title] - Wat de gebruiker in de prullenbak ziet
 * @param {string} [params.parentId] - Bij een cascade: het ID van de ouder
 * @returns {Promise<boolean>} of er iets is weggeschreven
 */
const softDeleteDoc = async ({ collection, docid, authId, title, parentId }) => {
    if (!collection || !docid) return false

    await db.collection(collection).doc(docid).update({
        Deleted: true,
        DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
        DeletedBy: authId || '',
    })

    // Pas registreren als het markeren gelukt is; anders staat er een regel in
    // de prullenbak voor iets wat nog gewoon in beeld is.
    await db.collection('DeletedItems').add({
        CompagnyID: client,
        Collection: collection,
        DocID: docid,
        // Zonder titel is de lijst een rij document-ids; de collectienaam is
        // dan het enige houvast dat de gebruiker heeft.
        Title: title || collection,
        DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
        DeletedBy: authId || '',
        ParentID: parentId || '',
    })

    return true
}

export default softDeleteDoc
